import { RENTAL_PORTAL_LISTING_STATUS } from '../../../../constants/rentalPortal'
import { ROUTE_PATH_LIST_RENTAL_PORTAL } from '../../../../constants/route'
import { formatNumberWithSeparators } from '../../../../utils/number'
import { BirdviewState, DashboardTileTone } from '../../../DashboardBirdview'
import {
  APPLICANT_STAGE,
  APPLICANTS_FILTER_PARAM,
} from '../../../RentalPortalApplicant'
import { LISTINGS_FILTER_PARAM } from '../../../RentalPortalListing'
import { VIEWINGS_FILTER_PARAM } from '../../../RentalPortalViewing'
import { getRentalPortalSubsectionLink } from '../../RentalPortal.utils'
import { LISTINGS_FILTER_TOGGLE_VALUE } from '../../RentalPortalListings'
import {
  OVERVIEW_KPI,
  OVERVIEW_PAYLOAD_FIELD,
} from '../RentalPortalOverview.constants'

import {
  OVERVIEW_KPI_ABBREVIATION_FORMAT_OPTIONS,
  OVERVIEW_KPI_ABBREVIATION_SUFFIX,
  OVERVIEW_KPI_ABBREVIATION_UNIT,
  OVERVIEW_KPI_TILE_LIST,
} from './RentalPortalOverviewKpiRow.constants'

function toOverviewKpiLink(subsection, params) {
  return `${getRentalPortalSubsectionLink(subsection)}?${new URLSearchParams(params)}`
}

function toOverviewKpiFieldLink({ field, overview }) {
  const scopeUserId = overview[OVERVIEW_PAYLOAD_FIELD.scopeUserId]

  switch (field) {
    case OVERVIEW_KPI.activeListings:
      return toOverviewKpiLink(ROUTE_PATH_LIST_RENTAL_PORTAL.listings, {
        [LISTINGS_FILTER_PARAM.manager]: scopeUserId,
        [LISTINGS_FILTER_PARAM.status]: RENTAL_PORTAL_LISTING_STATUS.published,
      })
    case OVERVIEW_KPI.applicantsInScreening:
      return toOverviewKpiLink(ROUTE_PATH_LIST_RENTAL_PORTAL.applicants, {
        [APPLICANTS_FILTER_PARAM.responsibleUser]: scopeUserId,
        [APPLICANTS_FILTER_PARAM.stage]: APPLICANT_STAGE.screening,
      })
    case OVERVIEW_KPI.listingsClosed:
      return toOverviewKpiLink(ROUTE_PATH_LIST_RENTAL_PORTAL.listings, {
        [LISTINGS_FILTER_PARAM.closedGte]:
          overview[OVERVIEW_PAYLOAD_FIELD.periodStart],
        [LISTINGS_FILTER_PARAM.closedLte]:
          overview[OVERVIEW_PAYLOAD_FIELD.periodEnd],
        [LISTINGS_FILTER_PARAM.manager]: scopeUserId,
        [LISTINGS_FILTER_PARAM.status]: RENTAL_PORTAL_LISTING_STATUS.closed,
      })
    case OVERVIEW_KPI.newApplicants:
      return toOverviewKpiLink(ROUTE_PATH_LIST_RENTAL_PORTAL.applicants, {
        [APPLICANTS_FILTER_PARAM.receivedGte]:
          overview[OVERVIEW_PAYLOAD_FIELD.periodStart],
        [APPLICANTS_FILTER_PARAM.receivedLte]:
          overview[OVERVIEW_PAYLOAD_FIELD.periodEnd],
        [APPLICANTS_FILTER_PARAM.responsibleUser]: scopeUserId,
        [APPLICANTS_FILTER_PARAM.stage]: APPLICANT_STAGE.pool,
      })
    case OVERVIEW_KPI.viewingsThisWeek:
      return toOverviewKpiLink(ROUTE_PATH_LIST_RENTAL_PORTAL.viewings, {
        [VIEWINGS_FILTER_PARAM.booked]: LISTINGS_FILTER_TOGGLE_VALUE,
        [VIEWINGS_FILTER_PARAM.responsibleUser]: scopeUserId,
        [VIEWINGS_FILTER_PARAM.startGte]:
          overview[OVERVIEW_PAYLOAD_FIELD.weekStart],
        [VIEWINGS_FILTER_PARAM.startLte]:
          overview[OVERVIEW_PAYLOAD_FIELD.weekEnd],
      })
    default:
      return null
  }
}

export function getOverviewKpiLink({ field, overview }) {
  return overview?.[OVERVIEW_PAYLOAD_FIELD.scopeUserId]
    ? toOverviewKpiFieldLink({ field, overview })
    : null
}

export function getOverviewKpiTileState({ isLoading, kpis }) {
  if (isLoading) {
    return BirdviewState.Loading
  }

  return kpis ? BirdviewState.Normal : BirdviewState.Error
}

export function checkIsOverviewKpiAbbreviated(value) {
  return value >= OVERVIEW_KPI_ABBREVIATION_UNIT
}

export function getOverviewKpiAbbreviatedCount(value) {
  return `${formatNumberWithSeparators(
    value / OVERVIEW_KPI_ABBREVIATION_UNIT,
    OVERVIEW_KPI_ABBREVIATION_FORMAT_OPTIONS
  )}${OVERVIEW_KPI_ABBREVIATION_SUFFIX}`
}

export function getOverviewKpiCount(value) {
  return checkIsOverviewKpiAbbreviated(value)
    ? getOverviewKpiAbbreviatedCount(value)
    : formatNumberWithSeparators(value)
}

export function getOverviewKpiDeltaTone(delta) {
  if (!delta) {
    return DashboardTileTone.Info
  }

  return delta > 0 ? DashboardTileTone.Success : DashboardTileTone.Error
}

export function getOverviewKpiDelta(delta) {
  return delta
    ? { isUp: delta > 0, text: formatNumberWithSeparators(Math.abs(delta)) }
    : null
}

export function getOverviewKpiTileId(tile) {
  return tile.field
}

export function getOverviewKpis({ isLoading, overview, t }) {
  const kpis = overview?.[OVERVIEW_PAYLOAD_FIELD.kpis]
  const kpisDelta = overview?.[OVERVIEW_PAYLOAD_FIELD.kpisDelta]
  const state = getOverviewKpiTileState({ isLoading, kpis })

  return OVERVIEW_KPI_TILE_LIST.map(({ Icon, field, labelKey, tone }) => ({
    Icon,
    delta: getOverviewKpiDelta(kpisDelta?.[field]),
    deltaTone: getOverviewKpiDeltaTone(kpisDelta?.[field]),
    field,
    label: t(labelKey),
    link: getOverviewKpiLink({ field, overview }),
    state,
    tone,
    value: kpis?.[field],
  }))
}
